import {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  loginApi,
  getCurrentUserApi,
} from "../api/auth/auth.api";

import {
  getToken,
  getStoredUser,
  setToken,
  setStoredUser,
  clearAuthStorage,
} from "../utils/storage";

const AuthContext = createContext(null);


export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    return getStoredUser();
  });

  const [loading, setLoading] = useState(true);

  /*
  |--------------------------------------------------------------------------
  | AUTHENTICATED STATE
  |--------------------------------------------------------------------------
  */

  const isAuthenticated = Boolean(
    getToken() && user
  );


  /*
  |--------------------------------------------------------------------------
  | LOAD CURRENT USER
  |--------------------------------------------------------------------------
  |
  | When the browser refreshes:
  |
  | localStorage token
  |        ↓
  | GET /auth/me
  |        ↓
  | backend verifies JWT
  |        ↓
  | current user
  |
  |--------------------------------------------------------------------------
  */

  const loadCurrentUser = async () => {
    const token = getToken();

    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await getCurrentUserApi();

      const currentUser = response.data;

      setUser(currentUser);
      setStoredUser(currentUser);
    } catch (error) {
      console.error(
        "Unable to restore authentication:",
        error
      );

      clearAuthStorage();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };


  /*
  |--------------------------------------------------------------------------
  | LOGIN
  |--------------------------------------------------------------------------
  */

  const login = async (email, password) => {
    const response = await loginApi({
      email,
      password,
    });

    const {
      token,
      user: loggedInUser,
    } = response.data;

    setToken(token);
    setStoredUser(loggedInUser);
    setUser(loggedInUser);

    return loggedInUser;
  };


  /*
  |--------------------------------------------------------------------------
  | LOGOUT
  |--------------------------------------------------------------------------
  */

  const logout = () => {
    clearAuthStorage();
    setUser(null);
  };


  /*
  |--------------------------------------------------------------------------
  | REFRESH AUTH
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    loadCurrentUser();
  }, []);


  /*
  |--------------------------------------------------------------------------
  | GLOBAL 401 LOGOUT
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const handleAuthLogout = () => {
      clearAuthStorage();
      setUser(null);
    };

    window.addEventListener(
      "auth:logout",
      handleAuthLogout
    );

    return () => {
      window.removeEventListener(
        "auth:logout",
        handleAuthLogout
      );
    };
  }, []);


  /*
  |--------------------------------------------------------------------------
  | CONTEXT VALUE
  |--------------------------------------------------------------------------
  */

  const value = {
    user,

    loading,

    isAuthenticated,

    login,

    logout,

    refreshUser: loadCurrentUser,
  };


  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};


/*
|--------------------------------------------------------------------------
| USE AUTH
|--------------------------------------------------------------------------
*/

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      "useAuth must be used inside AuthProvider"
    );
  }

  return context;
};

export default AuthContext;